<script lang="ts">
	import { MobilePodcastEpisodeCard } from '../../components/PodcastEpisodeCard/MobilePodcastEpisodeCard';
	import { TalkSpeakersAccordions } from '../TalkPage/TalkSpeakersAccordions';
	import type { withPodcastEpisodeTokens } from './utils/withPodcastEpisodeTokens/withPodcastEpisodeTokens';
	import { Config } from '#lib/constants/config.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { preserveGlobalNavCrumbs } from '#lib/features/navigation/utils/preserveGlobalNav/preserveGlobalNav.js';
	import { TalksRoutes } from '#lib/websites/talks/constants/routes.js';
	import { RelatedTalks } from '#lib/websites/talks/pages/TalkPage/RelatedTalks/index.js';
	import { TalkDetails } from '#lib/websites/talks/pages/TalkPage/TalkDetails/index.js';
	import { page } from '$app/state';
	import { ApplePodcastsIcon } from '$global/assets/icons/ApplePodcastsIcon';
	import { SpotifyIcon } from '$global/assets/icons/SpotifyIcon';
	import { YouTubeIcon } from '$global/assets/icons/YouTubeIcon';
	import { Breadcrumbs } from '$global/components/Breadcrumbs';
	import { Container } from '$global/components/Container';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { getPageData } from '$global/utils/getPageData/getPageData';
	import { toKebabCase } from '$global/utils/toKebabCase';
	import { track } from '$global/utils/track';
	import type { PodcastEpisodePageData } from '$routes/talks/podcasts/[slug]/episodes/[slug]/types';

	const dataCy = 'podcast-episode-page';
	let data = $derived(getPageData<PodcastEpisodePageData>(page.data));
	let podcastEpisode = $derived(data.podcastEpisode) as ReturnType<
		typeof withPodcastEpisodeTokens
	>;
	let artistSlugs = $derived(data.artistSlugs);

	let crumbs = $derived(
		preserveGlobalNavCrumbs(
			[
				{ label: 'Home', href: GlobalRoutes.Home },
				{ label: 'Talks', href: GlobalRoutes.TalksHome },
				{
					label: 'Podcasts',
					href: TalksRoutes.Podcasts,
				},
				{
					label: `${podcastEpisode.podcast?.title}`,
					href: `${TalksRoutes.Podcasts}/${podcastEpisode.podcast?.slug}`,
				},
				{
					label: podcastEpisode.title,
				},
			],
			page.url
		)
	);

	let sortedEpisodes = $derived(
		(podcastEpisode.podcast?.episodes || []).sort(
			(episodeA, episodeB) =>
				+new Date(episodeA?.publication_date) -
				+new Date(episodeB?.publication_date)
		)
	);

	let relatedEpisodes = $derived(
		(() => {
			const currentEpisodeIndex = sortedEpisodes.findIndex(
				(episode) => episode?.id === podcastEpisode?.id
			);

			return [
				...sortedEpisodes.slice(currentEpisodeIndex + 1),
				...sortedEpisodes.slice(0, currentEpisodeIndex),
			].slice(0, 9);
		})()
	);

	let relatedVideos = $derived(
		relatedEpisodes.map((episode) => ({
			title: `${episode?.title}`,
			image: getImageUrl(`${episode?.image?.filename_disk}`),
			description: `${episode?.short_description}`,
			duration: `${episode?.duration}`,
			href: `${TalksRoutes.Podcasts}/${podcastEpisode?.podcast?.slug}/episodes/${episode?.slug}`,
			id: `${episode?.id}`,
		}))
	);
</script>

<Breadcrumbs {dataCy} {crumbs} />
<Container {dataCy} class="mb-[8.75rem] lg:flex lg:space-x-6">
	<div class="lg:w-2/3">
		<TalkDetails
			type="podcast"
			speakers={(podcastEpisode.artists || []).map((artist) => ({
				name: `${artist?.Artists_id?.name}`,
				href: `${GlobalRoutes.ArtistsHome}/${toKebabCase(artist?.Artists_id?.name)}`,
			}))}
			images={podcastEpisode?.reference_images}
			shareUrl={`${Config.Domain}${TalksRoutes.Podcasts}/${podcastEpisode?.podcast?.slug}/episodes/${page.params.slug}`}
			timePeriod={null}
			filmDetails={podcastEpisode?.details}
			youtubeUrl={podcastEpisode?.youtube_link}
			furtherMedia={null}
			credits={podcastEpisode?.credits}
			transcript={podcastEpisode?.transcript}
			title={`${podcastEpisode?.title}`}
			detailsLabel="Details"
			{dataCy}
		>
			<div class="xs:mb-0 xs:ml-5 mb-3 flex gap-3">
				{#if podcastEpisode?.youtube_link}
					<button
						data-cy={`${dataCy}-youtube`}
						class="xs:flex-0 xs:border-0 flex flex-1 justify-center rounded border border-gray-200 py-2 hover:border-gray-700 focus:border-gray-500 active:border-gray-500"
						onclick={(e) => {
							e.preventDefault();

							track('TALKS/EPISODE_PAGE_YOUTUBE_CLICK', {
								slug: podcastEpisode?.slug,
							});

							window.open(
								podcastEpisode?.youtube_link,
								'_blank',
								'noopener noreferrer'
							);
						}}
					>
						<YouTubeIcon class="scale-[1.3]" hexCode="#FF0000" />
					</button>
				{/if}
				{#if podcastEpisode?.spotify_link}
					<button
						data-cy={`${dataCy}-spotify`}
						class="xs:flex-0 xs:border-0 flex flex-1 justify-center rounded border border-gray-200 py-2 hover:border-gray-700 focus:border-gray-500 active:border-gray-500"
						onclick={(e) => {
							e.preventDefault();

							track('TALKS/EPISODE_PAGE_SPOTIFY_CLICK', {
								slug: podcastEpisode?.slug,
							});

							window.open(
								podcastEpisode?.spotify_link as string,
								'_blank',
								'noopener noreferrer'
							);
						}}
					>
						<SpotifyIcon />
					</button>
				{/if}

				{#if podcastEpisode?.apple_podcasts_link}
					<button
						data-cy={`${dataCy}-apple-podcasts`}
						class="xs:flex-0 xs:border-0 flex flex-1 justify-center rounded border border-gray-200 py-2 hover:border-gray-700 focus:border-gray-500 active:border-gray-500"
						onclick={(e) => {
							e.preventDefault();

							track('TALKS/EPISODE_PAGE_APPLE_CLICK', {
								slug: podcastEpisode?.slug,
							});

							window.open(
								podcastEpisode?.apple_podcasts_link as string,
								'_blank',
								'noopener noreferrer'
							);
						}}
					>
						<ApplePodcastsIcon />
					</button>
				{/if}
			</div>
		</TalkDetails>
		<TalkSpeakersAccordions
			{artistSlugs}
			{dataCy}
			podcastOpenpanelPrefix="TALKS/EPISODE_PAGE"
		/>
	</div>
	<div class="lg:w-1/3">
		<RelatedTalks type="podcast" {relatedVideos} {dataCy} />
		<div
			data-cy={dataCy}
			class="xs:grid-cols-1 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:hidden"
		>
			{#each relatedEpisodes as relatedEpisode}
				<MobilePodcastEpisodeCard podcastEpisode={relatedEpisode} />
			{/each}
		</div>
	</div>
</Container>
